import React from 'react';
import { object, string, shape, number, bool, func } from 'prop-types';

import { surveyJobMatches as surveyJobMatchesPropType, surveyJobMatch as surveyJobMatchPropType } from '../propTypes';

import SurveyJobMatchesHeader from './SurveyJobMatchHeaders/SurveyJobMatchesHeader';
import SurveyJobDrilldownHeader from './SurveyJobMatchHeaders/SurveyJobDrilldownHeader';
import SurveyJobMatch from './SurveyJobMatch/SurveyJobMatch';
import SurveyJobDrilldown from './SurveyJobDrilldown/SurveyJobDrilldown';
import SurveyJobInformation from './SurveyJobInformation/SurveyJobInformation';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';

import './SurveyJobMatches.scss';

class SurveyJobMatches extends React.Component {
  static propTypes = {
    surveyJobMatches: surveyJobMatchesPropType,
    drilldownJob: surveyJobMatchPropType,
    loading: bool,
    actions: object,
    selectedPayElement: string,
    payElementLabels: shape({
      baseSalary: string,
      totalCash: string,
      totalTargetCash: string,
      totalDirect: string,
      totalGuaranteedPay: string,
      totalEmployeeCount: string,
      totalCompanyCount: string
    }),
    benchmarkYear: number,
    percentRounding: number,
    monetaryRounding: number,
    errorLoadingSurveyJobMatches: bool,
    showDrilldown: bool,
    showSurveyJobDrilldownAction: func,
    showSurveyJobMatches: func,
    chartData: object,
    showAsPercentage: bool,
    isPdf: bool,
    showOverallComposites: bool,
    metadata: object
  };

  getSurveyJobMatchesRender() {
    const {
      selectedPayElement,
      surveyJobMatches,
      showSurveyJobDrilldownAction,
      monetaryRounding,
      metadata
    } = this.props;
    return surveyJobMatches.map((job, index) => {
      return (
        <SurveyJobMatch
          key={`${job.metadata.surveyJobCode}-${index}`}
          surveyJob={job}
          pricingData={metadata.pricingData}
          selectedPayElement={selectedPayElement}
          showSurveyJobDrilldownAction={showSurveyJobDrilldownAction}
          monetaryRounding={monetaryRounding}
        />
      );
    });
  }

  getSurveyJobDrilldownRender() {
    const {
      actions,
      drilldownJob,
      showSurveyJobMatches,
      selectedPayElement,
      showAsPercentage,
      monetaryRounding,
      percentRounding,
      chartData,
      showOverallComposites,
      metadata
    } = this.props;
    return (
      <SurveyJobDrilldown
        pricingData={metadata.pricingData}
        surveyJob={drilldownJob}
        backAction={showSurveyJobMatches}
        selectedPayElement={selectedPayElement}
        monetaryRounding={monetaryRounding}
        percentRounding={percentRounding}
        chartData={chartData}
        showAsPercentage={showAsPercentage}
        toggleShowAsPercentage={actions.toggleShowAsPercentage}
        showOverallComposites={showOverallComposites}
      />
    );
  }

  getSurveyJobMatchesHeaderRender() {
    const { selectedPayElement, payElementLabels, actions, benchmarkYear, isPdf, showOverallComposites } = this.props;
    return (
      !isPdf && (
        <SurveyJobMatchesHeader
          selectedPayElement={selectedPayElement}
          showOverallComposites={showOverallComposites}
          payElementLabels={payElementLabels}
          selectPayElement={actions.selectPayElement}
          benchmarkYear={benchmarkYear}
        />
      )
    );
  }

  getSurveyDrilldownHeaderRender() {
    const { selectedPayElement, payElementLabels, actions, drilldownJob, isPdf } = this.props;
    return !isPdf ? (
      <SurveyJobDrilldownHeader
        backAction={actions.showSurveyJobMatches}
        surveyJob={drilldownJob}
        selectedPayElement={selectedPayElement}
        payElementLabels={payElementLabels}
        selectPayElement={actions.selectPayElement}
      />
    ) : (
      <div className="survey-job-matches__header survey-job-drilldown__header">
        <SurveyJobInformation metadata={drilldownJob.metadata} />
      </div>
    );
  }

  getLoadingRender() {
    const header = this.getSurveyJobMatchesHeaderRender();

    return (
      <React.Fragment>
        {header}
        <div className="survey-job-matches__content">
          <div className="survey-job-matches survey-job-matches__is-loading">
            <InPlaceIndicator />
          </div>
        </div>
      </React.Fragment>
    );
  }

  getErrorRender() {
    const header = this.getSurveyJobMatchesHeaderRender();

    return (
      <React.Fragment>
        {header}
        <div className="survey-job-matches__content">
          <div className="survey-job-matches__no-data">
            <h3 className="pxl-text-body survey-job-matches__no-data-header">
              There was an unexpected error loading survey job matches for this org job. Please try again later.
            </h3>
          </div>
        </div>
      </React.Fragment>
    );
  }

  getSlottedMatchDataRender() {
    return (
      <div className="survey-job-matches__content">
        <div className="survey-job-matches__no-data">
          <div>
            <h3 className="pxl-text-body survey-job-matches__no-data-header">
              Survey Job Matches Not Available for Slotted Job
            </h3>
            <p className="pxl-text-small survey-job-matches__no-data-text">
              Please change benchmark year to see available Survey Job Matches
            </p>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { loading, errorLoadingSurveyJobMatches, showDrilldown, benchmarkYear, metadata } = this.props;
    const isSlottedJobMatch = metadata.pricingData.some(
      row => parseInt(row.benchmarkYear) === parseInt(benchmarkYear) && row.isSlotted
    );

    if (isSlottedJobMatch) {
      return this.getSlottedMatchDataRender();
    }

    if (loading) {
      return this.getLoadingRender();
    }

    if (errorLoadingSurveyJobMatches) {
      return this.getErrorRender();
    }

    const components = showDrilldown ? this.getSurveyJobDrilldownRender() : this.getSurveyJobMatchesRender();
    const header = showDrilldown ? this.getSurveyDrilldownHeaderRender() : this.getSurveyJobMatchesHeaderRender();

    return (
      <React.Fragment>
        {header}
        <div className="survey-job-matches__content">
          <div className="survey-job-matches">{components}</div>
        </div>
      </React.Fragment>
    );
  }
}

export default SurveyJobMatches;
